"use client";

import Image from "next/image";
import { useCart } from "@/cart";
import {
  BookStencil,
  Canvas,
  Card,
  Typography,
} from "@cappelendamm/design-system/react";
import { PiTrash } from "react-icons/pi";
import classNames from "classnames";
import { getCartNumberOfItems, isFreightItem } from "@/cart/selectors";
import ProductLink from "@/products/components/ProductLink";

export function EvaluationCartItems({ className }: { className?: string }) {
  const { cart, removeItem, isPending } = useCart();
  if (getCartNumberOfItems(cart) === 0) {
    if (isPending) {
      return <Typography variant="body-lg">Laster...</Typography>;
    }
    return (
      <div className="mt-8 mb-auto text-center">
        <Typography as="h2" variant="title-lg">
          Listen er tom
        </Typography>
      </div>
    );
  }

  return (
    <Card className={classNames("max-h-full", className)} as={"ul"}>
      {cart?.items
        .filter((item) => !isFreightItem(item))
        .map(
          ({
            images,
            variant: {
              sku,
              product: { path },
              name: variantName,
              attributes,
            },
          }) => {
            const authors = attributes?.Forfattervisning;
            const language = attributes?.Språk;

            const itemDetails = [authors, language].filter(Boolean).join(", ");
            return (
              <li
                key={sku}
                className={classNames(
                  "border-b py-4 first-of-type:pt-0 last-of-type:border-none last-of-type:pb-0",
                  { "animate-pulse": path === "loading" },
                )}
              >
                <article className="grid grid-cols-[auto_1fr_auto] items-center gap-4">
                  <Canvas className="p-3">
                    {images?.[0]?.url ? (
                      <Image
                        src={images?.[0]?.url || ""}
                        height={200}
                        width={84}
                        alt={`Omslag av ${variantName}`}
                      />
                    ) : (
                      <BookStencil
                        title=""
                        style={
                          {
                            "--book-width": `84px`,
                          } as React.CSSProperties
                        }
                      />
                    )}
                  </Canvas>
                  <ProductLink sku={sku}>
                    <Typography className="mb-1" variant="title-sm">
                      {variantName}
                    </Typography>
                    {itemDetails && <Typography>{itemDetails}</Typography>}
                  </ProductLink>

                  <button
                    className="mr-5 rounded-xl p-2 hover:cursor-pointer hover:bg-gray-50 focus:cvr-focus disabled:opacity-25"
                    type="button"
                    disabled={isPending}
                    onClick={async () => {
                      await removeItem({ sku });
                    }}
                  >
                    <span className="sr-only">{`Fjern ${name}`}</span>
                    <PiTrash size={24} />
                  </button>
                </article>
              </li>
            );
          },
        )}
    </Card>
  );
}
